import type { PersonalityBias, PersonalityName } from './types';

/**
 * Spec §4.6 five MVP characters. Each personality is a set of biases on
 * top of the baseline GTO decision.
 *
 * aggression: raise/bet frequency (-1..+1)
 * looseness: preflop VPIP range expansion (-1..+1)
 * bluffFreq: postflop bluff-only adjustment (-1..+1)
 * tilt: transient post-badbeat state (0..1, decays over hands)
 */
export const PERSONALITIES: Readonly<Record<PersonalityName, PersonalityBias>> = {
  VOID: { aggression: 0, looseness: -0.3, bluffFreq: -0.2, tilt: 0 },
  GRIM: { aggression: 0.4, looseness: 0, bluffFreq: 0.3, tilt: 0 },
  PALE: { aggression: -0.3, looseness: 0.4, bluffFreq: -0.1, tilt: 0 },
  JEST: { aggression: 0.2, looseness: 0.3, bluffFreq: 0.7, tilt: 0.2 },
  ORACLE: { aggression: 0, looseness: 0, bluffFreq: 0, tilt: 0 },
};

/**
 * Adjust a pre-decision aggressive-action frequency by the personality biases.
 * Returns a clamped number in [0, 1].
 */
export function biasAggressiveFreq(base: number, bias: PersonalityBias): number {
  const aggDelta = bias.aggression * 0.35; // max ±0.35
  const tiltDelta = bias.tilt * 0.25;
  const shifted = base + aggDelta + tiltDelta;
  return Math.max(0, Math.min(1, shifted));
}

/**
 * Adjust preflop VPIP gate by looseness. Returns a frequency in [0, 1].
 */
export function biasVpipFreq(base: number, bias: PersonalityBias): number {
  const delta = bias.looseness * 0.4;
  return Math.max(0, Math.min(1, base + delta));
}

/**
 * Bluff frequency adjustment when we want to represent a hand we don't have.
 * Returns a frequency in [0, 1].
 */
export function biasBluffFreq(base: number, bias: PersonalityBias): number {
  const delta = bias.bluffFreq * 0.4;
  return Math.max(0, Math.min(1, base + delta));
}

/**
 * Tilt mutation: increase tilt by 0.3 (capped 1.0) and decay back by 0.06
 * per subsequent hand. Deterministic, side-effect-free (returns new bias).
 */
export function applyTilt(bias: PersonalityBias, event: 'big-loss' | 'normal'): PersonalityBias {
  if (event === 'big-loss') {
    return { ...bias, tilt: Math.min(1, bias.tilt + 0.3) };
  }
  return { ...bias, tilt: Math.max(0, bias.tilt - 0.06) };
}
